import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAccess, useBindings, useClearOverride, useCustomer, useOverrides, useProfiles } from "../../api/hooks";
import { NotHeld } from "../../components/NotHeld";
import { EnvironmentGate, useRememberEnvironment } from "../../components/Environment";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, MutationError, PageHeader, RelativeTime, StatusPill, btnPrimary, btnSecondary } from "../../components/ui";
import { moduleById } from "../../lib/modules";
import { recentProfiles } from "../../lib/recent-profiles";
import { isClosure } from "./EmergencyClose";
import { ContextBar } from "./ContextBar";
import { ImportProfile } from "./ImportExport";
import { NewProfileForm } from "./parts";

/**
 * The way in at three hundred profiles (requirements 8.9): a searchable
 * table with recents, never a list in the nav. What a person cannot see is
 * not here at all (ADR-0017 section 2b, when it lands).
 */
export function ProfilesPage() {
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="profiles" />;
  return <ProfilesList environmentId={environmentId} />;
}

function ProfilesList({ environmentId }: { environmentId: string }) {
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const bindings = useBindings(environmentId);
  const overrides = useOverrides(environmentId);
  const clear = useClearOverride(environmentId);
  const access = useAccess();
  const canCreate = access.can(environmentId, "configuration.profiles:create");
  const canReopen = access.can(environmentId, "configuration.overrides:edit");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const enabled = env.data?.enabledComponents.includes("FlowConfiguration") ?? true;
  const recent = recentProfiles(environmentId);
  const base = `/flow-configuration/${environmentId}/profiles`;
  const missing = (useLocation().state as { missingProfile?: string | true } | null)?.missingProfile;
  const boundCount = (profileId: string) => (bindings.data?.bindings ?? []).filter((b) => b.profileId === profileId).length;
  // An emergency close is an override on an hours field whose week is empty (EmergencyClose.tsx).
  const closedBy = (profileId: string) => (overrides.data?.overrides ?? []).find((o) => o.profileId === profileId && o.field.startsWith("hours/") && isClosure(o.value));

  return (
    <>
      <PageHeader title="Flow configuration · Profiles" />
      <div className="grid gap-5">
        {missing && (
          <p role="status" className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
            {typeof missing === "string" ? <><span className="font-medium">{missing}</span> is not</> : "That profile is not"} a profile of {env.data?.customer.friendly_name ?? "this environment"}&rsquo;s organization. Choose one below.
          </p>
        )}
        <ContextBar
          environmentId={environmentId}
          environmentName={env.data?.customer.friendly_name}
          live={env.data?.customer.live ?? false}
          onEnvironment={(id) => `/flow-configuration/${id}/profiles`}
          actions={
            enabled && canCreate ? (
              <>
                <button type="button" className={btnSecondary} onClick={() => { setImporting((v) => !v); setCreating(false); }} aria-expanded={importing}>
                  Import
                </button>
                <button type="button" className={btnPrimary} onClick={() => { setCreating((v) => !v); setImporting(false); }} aria-expanded={creating}>
                  New profile
                </button>
              </>
            ) : enabled && !access.isPending ? (
              <NotHeld permission="configuration.profiles:create" />
            ) : null
          }
        >
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {profiles.data ? `${profiles.data.profiles.length} profile${profiles.data.profiles.length === 1 ? "" : "s"}` : ""}
          </span>
          {recent.length > 0 && profiles.data && (
            <span className="text-sm text-neutral-600 dark:text-neutral-400">
              Recent:{" "}
              {recent
                .map((id) => profiles.data.profiles.find((p) => p.profileId === id))
                .filter((p): p is NonNullable<typeof p> => Boolean(p))
                .map((p, i) => (
                  <span key={p.profileId}>
                    {i > 0 && " · "}
                    <Link to={`${base}/${p.profileId}`} className="underline">{p.name}</Link>
                  </span>
                ))}
            </span>
          )}
        </ContextBar>

        {env.data && !enabled && (
          <EmptyState title="Flow configuration is not deployed in this account" hint="Redeploy the customer template with EnableFlowConfiguration set to true. It needs a Connect instance id." />
        )}
        {creating && <NewProfileForm environmentId={environmentId} onCreated={() => setCreating(false)} onCancel={() => setCreating(false)} />}
        {importing && <ImportProfile environmentId={environmentId} onDone={() => setImporting(false)} />}

        {enabled && profiles.isPending && <LoadingState label="Loading profiles" />}
        {profiles.isError && <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />}
        {enabled && profiles.data && (
          <Card>
            <DataTable
              caption="Profiles in this environment"
              rows={profiles.data.profiles}
              rowKey={(p) => p.profileId}
              sortBy={(p) => p.name}
              empty={
                <EmptyState
                  title="No profiles yet"
                  hint="A profile is the configuration for one line your callers dial - its opening hours, holidays, announcements, menus and where calls go - so the contact flow never has to change. 'Global Helpdesk' or 'Sales' would each be one. It is written once and works in every environment; a binding decides which aliases - numbers, or names a flow passes - reach it. Start with a name; the rest is added afterwards."
                />
              }
              columns={[
                { key: "name", header: "Profile", render: (p) => <Link to={`${base}/${p.profileId}`} className="font-medium text-accent-strong underline dark:text-accent-bright">{p.name}</Link> },
                { key: "description", header: "", render: (p) => <span className="text-neutral-500">{p.description ?? ""}</span> },
                { key: "aliases", header: "Aliases", numeric: true, render: (p) => (bindings.data ? boundCount(p.profileId) : "…") },
                {
                  key: "closed",
                  header: "Emergency close",
                  render: (p) => {
                    const o = closedBy(p.profileId);
                    if (!o) return <span className="text-xs text-neutral-500">-</span>;
                    return (
                      <span className="flex flex-wrap items-center gap-2">
                        <StatusPill status="closed" tone="critical" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">{o.reason} · {o.setBy}</span>
                        {canReopen && (
                          <button type="button" className={`${btnSecondary} min-h-7 px-2 text-xs`} disabled={clear.isPending} onClick={() => clear.mutate({ profileId: p.profileId, field: o.field })} aria-label={`Reopen ${p.name}`}>
                            Reopen
                          </button>
                        )}
                      </span>
                    );
                  },
                },
                { key: "version", header: "Version", render: (p) => (p.configVersion ? `v${p.configVersion}` : <StatusPill status="not published here" tone="neutral" />) },
                { key: "changed", header: "Last change", render: (p) => (p.publishedAt ? <>{p.publishedBy} · <RelativeTime iso={p.publishedAt} /></> : "-") },
              ]}
            />
            <MutationError of={clear} className="mt-3" />
          </Card>
        )}
      </div>
    </>
  );
}
